import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useOrganizationService } from '../../services';
import { useJwt } from '../../helpers/common';
import { Label, Tooltip } from "flowbite-react";
// @ts-ignore - DatePicker types not available
import DatePicker from "react-datepicker";
import toast from 'react-hot-toast';
import {
  HiQuestionMarkCircle,
  HiTrash
} from "react-icons/hi";
import RichTextEditor from "../RichTextEditor";
import {Drawer} from "../Modals";
import CustomButton from '../common/CustomButton';
import FormInput from '../common/FormInput';
import CustomDropdown from '../common/CustomDropdown';
import NumberInput from '../common/NumberInput';
import MultiSelect from '../common/MultiSelect';
import Portal from '../Portal';

interface SimulationTaskModalProps {
  selectedTask?: any;
  showModal?: boolean;
  isVisiable?: boolean;
  onClose?: (data?: any) => void;
  onTaskUpdate?: (data?: any) => void;
  tasks?: any[];
  skills?: any[];
  members?: any[];
}

const SimulationTaskModal: React.FC<SimulationTaskModalProps> = ({
  selectedTask,
  isVisiable,
  onClose,
  onTaskUpdate,
  tasks = [],
  skills = [],
  members = []
}) => {
  const [task_title, setTitle] = useState(selectedTask?.task_title || selectedTask?.name || '');
  const [description, setDescription] = useState(selectedTask?.description || '');
  const [task_type, setTask_type] = useState(selectedTask?.task_type || "task");
  const [duration, setDuration] = useState(selectedTask?.duration || 1);
  const [skill_required, setSkillRequired] = useState(selectedTask?.skill_required || '');
  const [float_start_date, setFloatStartDate] = useState(selectedTask?.float_start_date ? new Date(selectedTask.float_start_date) : null);
  const [due_date, setDueDate] = useState(selectedTask?.due_date ? new Date(selectedTask.due_date) : null);
  const [dependenciesIds, setDependenciesIds] = useState(selectedTask?.dependency || []);
  const [completion, setCompletion] = useState(selectedTask?.completion || 0);
  const [responsible, setResponsible] = useState(selectedTask?.responsible || '');
  const [additional_resources, setAdditionalResources] = useState(selectedTask?.additional_resources || []);
  const [bid_price, setBidPrice] = useState(selectedTask?.bid_price || 0);
  const [billing_price, setBillingPrice] = useState(selectedTask?.billing_price || 0);

  const [validationErrors, setValidationErrors] = useState({
    title: false,
    description: false,
    skill: false,
    start_date: false
  });

  const orgSkills = useSelector((state: any) => state.organization?.skills) || [];
  const skillLoading = useSelector((state: any) => state.organization?.skillLoading) || false;
  const { fetchOrganizationSkill } = useOrganizationService();
  const JWTdata: any = useJwt();
  const availableSkills = orgSkills.length > 0 ? orgSkills : (skills || []);

  const memoizedFetchSkills = useCallback(() => {
    if (JWTdata?.org_id) {
      fetchOrganizationSkill(JWTdata.org_id);
    }
  }, [JWTdata?.org_id, fetchOrganizationSkill]);

  const findMatchingOrgSkill = (taskSkill: string) => {
    return orgSkills.find((skill: any) => {
      const skillName = skill.skill || skill.name || '';
      const skillId = skill.id?.toString() || '';
      
      return skillName.toLowerCase() === taskSkill.toLowerCase() ||
             skillId === taskSkill ||
             skillName === taskSkill;
    });
  };

  useEffect(() => {
    if (isVisiable && orgSkills.length === 0 && !skillLoading) {
      memoizedFetchSkills();
    }
  }, [isVisiable, orgSkills.length, skillLoading, memoizedFetchSkills]);

  useEffect(() => {
    if (selectedTask) {
      setTitle(selectedTask?.task_title || selectedTask?.name || '');
      setDescription(selectedTask?.description || '');
      setTask_type(selectedTask?.task_type || "task");
      setDuration(selectedTask?.duration || 1);
      setFloatStartDate(selectedTask?.float_start_date ? new Date(selectedTask.float_start_date) : null);
      setDueDate(selectedTask?.due_date ? new Date(selectedTask.due_date) : null);
      setDependenciesIds(selectedTask?.dependency || []);
      setCompletion(selectedTask?.completion || 0);
      setResponsible(selectedTask?.responsible || '');
      setAdditionalResources(selectedTask?.additional_resources || []);
      setBidPrice(selectedTask?.bid_price || 0);
      setBillingPrice(selectedTask?.billing_price || 0);
    }
  }, [selectedTask]);

  useEffect(() => {
    if (selectedTask && orgSkills.length > 0) {
      const taskSkill = selectedTask?.skill_required || '';
      
      if (taskSkill) {
        const matchingOrgSkill = findMatchingOrgSkill(taskSkill);
        
        if (matchingOrgSkill) {
          setSkillRequired(matchingOrgSkill.skill || matchingOrgSkill.name || matchingOrgSkill.id.toString());
        } else {
          setSkillRequired(taskSkill);
        }
      }
    } else if (selectedTask) {
      setSkillRequired(selectedTask?.skill_required || '');
    }
  }, [selectedTask, orgSkills.length]);

  const onSubmit = () => {
    setValidationErrors({
      title: false,
      description: false,
      skill: false,
      start_date: false
    });

    if (!task_title) {
      setValidationErrors(prev => ({ ...prev, title: true }));
      toast.error("Title cannot be null/empty!", { duration: 1000 });
      return;
    }

    if (task_type === "float" && !float_start_date) {
      setValidationErrors(prev => ({ ...prev, start_date: true }));
      toast.error("Start date is required for float tasks!", { duration: 1000 });
      return;
    }

    const strippedDescription = description?.replace(/<[^>]*>/g, '').trim();
    if (!description || !strippedDescription || description === '<p></p>' || description === '<p><br></p>' || description === '<p><br/></p>') {
      setValidationErrors(prev => ({ ...prev, description: true }));
      toast.error("Description cannot be null/empty!", { duration: 1000 });
      return;
    }

    // Create the updated task data
    const updatedTaskData = {
      task_id: selectedTask?.task_id || selectedTask?.id,
      task_title,
      description,
      duration,
      skill_required,
      task_type,
      dependency: dependenciesIds,
      float_start_date: task_type === 'float' && float_start_date ? 
        float_start_date.toISOString().split('T')[0] : undefined,
      due_date: due_date ? due_date.toISOString().split('T')[0] : undefined,
      completion,
      responsible,
      additional_resources,
      bid_price,
      billing_price,
      gpt_update_type: 'update' as 'update'
    };

    // Call the update callback with the data
    if (onTaskUpdate) {
      onTaskUpdate(updatedTaskData);
    }
    
    handleCloseModal();
  };

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleSelectChange = (value: any[]) => {
    const ids = value.map(el => el.value);
    setDependenciesIds(ids);
  };

  const handleAdditionalResourcesSelect = (value: any[]) => {
    const ids = value.map(el => el.value);
    setAdditionalResources(ids);
  };

  const handleCompletionChange = (e: { target: { value: any; }; }) => {
    const val = e.target.value;
    if(val > 100){
      setCompletion(100);
    } else if(val < 0){
      setCompletion(0);
    } else {
      setCompletion(val);
    }
  };

  const handleDurationChange = (e: { target: { value: any; }; }) => {
    const val = e.target.value;
    if(val < 0){
      setDuration(0);
    } else {
      setDuration(val);
    }
  };

  const handleBidPriceChange = (e: { target: { value: any; }; }) => {
    const val = e.target.value;
    if(val < 0){
      setBidPrice(0);
    } else if(val > 10000){
      setBidPrice(10000);
    } else {
      setBidPrice(val);
    }
  };

  const handleBillingPriceChange = (e: { target: { value: any; }; }) => {
    const val = e.target.value;
    if(val < 0){
      setBillingPrice(0);
    } else if(val > 10000){
      setBillingPrice(10000);
    } else {
      setBillingPrice(val);
    }
  };

  const availableMembers = members || [];

  // Task types
  const taskTypes = [
    { value: "task", label: "Task" },
    { value: "float", label: "Float" }
  ].sort((a, b) => a.label.localeCompare(b.label));

  // Sort tasks options for dependencies
  const sortedTasksOption = tasks
    .filter(({id, item_id, task_id}) => {
      const taskItemId = id || item_id || task_id;
      const currentTaskId = selectedTask?.id || selectedTask?.item_id || selectedTask?.task_id;
      return taskItemId !== currentTaskId;
    })
    .map(({name, id, item_id, task_id, task_title}) => {
      const taskItemId = id || item_id || task_id;
      const taskName = name || task_title || `Task ${taskItemId}`;
      return {
        value: taskItemId,
        label: `${taskItemId} : ${taskName}`
      };
    })
    .sort((a, b) => a?.label?.localeCompare(b?.label));

  // Sort resources for assignee dropdowns  
  const sortedResponsibleArr = [...availableMembers]
    .filter((r: any) => !r?.is_deleted)
    .sort((a, b) => a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase()));

  // Additional resources array (exclude selected assignee)
  const sortedAdditionalResourceArr = [...availableMembers]
    .filter((r: any) => !r?.is_deleted && Number(r.id) !== Number(responsible))
    .map(resource => ({
      label: resource.email ? `${resource.name} (${resource.email})` : resource.name,
      value: resource.id
    }))
    .sort((a, b) => a?.label?.toLowerCase().localeCompare(b?.label?.toLowerCase()));

  // Get assignee name for display
  const selectedAssignee = availableMembers.find((r: any) => r?.id?.toString() === responsible?.toString());
  const selectedAssigneeLabel = selectedAssignee
    ? (selectedAssignee.email ? `${selectedAssignee.name} (${selectedAssignee.email})` : selectedAssignee.name)
    : 'Not assigned';

  const isWeekday = (date: any) => {
    const day = date.getDay();
    return day !== 0 && day !== 6;
  };

  return (
    <Portal>
      <Drawer
        isOpen={isVisiable}
        onClose={handleCloseModal}
        position="right"
      >
        <div className="relative h-[calc(100%-55px)] overflow-auto flex flex-col">
          <div className={`flex flex-col border-b bg-light-surfacePrimary border-gray-500 dark:border-gray-700 !p-6 dark:bg-tertiary-dark sticky top-0 z-40 shadow-lg rounded-b-2xl`}>
            <div className="flex w-full items-start justify-between mb-2">
              <div className="flex flex-col min-w-0 max-w-[calc(100%-80px)]">
                <div className="flex items-center gap-3 mb-1">
                  <div className="text-light-text-secondary dark:text-white/70 mb-1 text-xs font-medium leading-none tracking-wide shrink-0">
                    {selectedTask?.task_id || selectedTask?.id} • {duration} days • Simulation
                  </div>
                </div>

                <div className="text-light-text-primary dark:text-white text-lg font-semibold font-['Inter'] leading-tight truncate pr-2">
                  {task_title || 'Edit Task'}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
                  onClick={handleCloseModal}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                  </svg>
                </button>
              </div>
            </div>

            <div className="text-light-text-tertiary dark:text-white/60 text-sm mb-3">
              Simulation Mode - No API calls will be made
            </div>
          </div>

          <div className='px-16 xs:px-4 py-6 bg-light-surfacePrimary dark:bg-tertiary-dark'>
            <div className="flex flex-col gap-5 max-w-4xl mx-auto">
              <FormInput
                id="title"
                name="title"
                label="Title"
                type="text"
                value={task_title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (e.target.value) {
                    setValidationErrors(prev => ({ ...prev, title: false }));
                  }
                }}
                placeholder="Enter task title..."
                required
                showAsterisk
                className={`${
                  validationErrors.title
                    ? 'border-[3px] border-light-status-error-border ring-4 ring-light-status-error-border/20 dark:border-red-500'
                    : 'border border-light-border-default dark:border-gray-600'
                } bg-light-surfaceSecondary dark:bg-slate-800 text-light-text-primary dark:text-white focus:ring-primary-light focus:border-primary-light dark:focus:ring-primary-dark dark:focus:border-primary-dark placeholder-light-input-placeholder dark:placeholder-gray-400 transition-all duration-200`}
              />

              <div>
                <Label className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white block" htmlFor="description">Description *</Label>
                <div className={`mt-1 ${
                  validationErrors.description
                    ? 'border-[3px] border-light-status-error-border ring-4 ring-light-status-error-border/20 dark:border-red-500'
                    : ''
                } rounded-lg overflow-hidden`}>
                  <RichTextEditor
                    content={description}
                    onChange={(content) => {
                      setDescription(content);
                      const strippedContent = content?.replace(/<[^>]*>/g, '').trim();
                      if (strippedContent) {
                        setValidationErrors(prev => ({ ...prev, description: false }));
                      }
                    }}
                    placeholder="Enter task description..."
                    disabled={false}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <CustomDropdown
                  id="skill"
                  name="skill"
                  label="Skill Required"
                  value={skill_required}
                  onChange={(value) => {
                    setSkillRequired(value);
                    if (value) {
                      setValidationErrors(prev => ({ ...prev, skill: false }));
                    }
                  }}
                  options={availableSkills
                    .map((skill: any) => ({
                      value: skill.skill || skill.name || skill.id.toString(),
                      label: skill.skill || skill.name || `Skill ${skill.id}`
                    }))
                    .sort((a: any, b: any) => a.label.localeCompare(b.label))
                  }
                  placeholder="Choose a Skill"
                  className={`!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200 ${
                    validationErrors.skill
                      ? 'border-[3px] border-light-status-error-border ring-4 ring-light-status-error-border/20 dark:border-red-500'
                      : ''
                  }`}
                />

                <CustomDropdown
                  id="TaskType"
                  name="task_type"
                  label="Task Type *"
                  value={task_type}
                  onChange={(value) => {
                    if (task_type === 'float' && value === 'task') {
                      setFloatStartDate(null);
                    }
                    setTask_type(value);
                  }}
                  options={taskTypes}
                  placeholder="Choose a Task Type"
                  required
                  className="!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200"
                />
              </div>

              {task_type !== 'float' ? (
                <MultiSelect
                  id="dependencies"
                  name="dependencies"
                  label="Dependent On"
                  value={sortedTasksOption.filter((ele) => dependenciesIds.includes(ele.value))}
                  onChange={handleSelectChange}
                  options={sortedTasksOption}
                  placeholder="Select dependencies"
                  className="!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200"
                />
              ) : (
                <div className="w-full">
                  <Label
                    className="block mb-2 text-sm font-medium text-light-text-primary dark:text-white"
                    htmlFor="float_start_date"
                  >
                    Start Date *
                  </Label>
                  <div className="mt-2">
                    <DatePicker
                      id="float_start_date"
                      wrapperClassName="w-full"
                      className={`w-full p-2.5 text-sm rounded-lg transition-all duration-200 ${
                        validationErrors.start_date
                          ? 'border-[3px] border-light-status-error-border ring-4 ring-light-status-error-border/20 dark:border-red-500'
                          : 'border border-light-border-default dark:border-gray-600'
                      } bg-light-surfaceSecondary text-light-text-primary focus:ring-primary-light focus:border-primary-light dark:bg-slate-800 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark`}
                      selected={float_start_date}
                      onChange={(date: any) => {
                        setFloatStartDate(date);
                        if (date) {
                          setValidationErrors(prev => ({ ...prev, start_date: false }));
                        }
                      }}
                      filterDate={isWeekday}
                      minDate={new Date()}
                      autoComplete='off'
                      placeholderText="Select start date"
                      customInput={
                        <input
                          style={{ width: '100%' }}
                          className="w-full p-2.5 text-sm rounded-lg border border-light-border-default bg-light-surfaceSecondary text-light-text-primary focus:ring-primary-light focus:border-primary-light dark:bg-slate-800 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark transition-all duration-200"
                        />
                      }
                    />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white block" htmlFor="due_date">Due Date</Label>
                <div className="mt-1">
                  <DatePicker
                    id="due_date"
                    className="bg-light-surfaceSecondary border border-light-border-default text-light-text-primary text-sm block w-full p-2.5 dark:bg-slate-800 dark:border-gray-600 dark:text-white rounded-lg cursor-not-allowed opacity-60"
                    selected={due_date}
                    onChange={() => {}} // Disabled - no onChange
                    disabled={true}
                    autoComplete='off'
                    placeholderText="Due date not editable in simulation"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <NumberInput
                  id="duration"
                  name="duration"
                  label="Duration (In Days)"
                  value={duration}
                  onChange={handleDurationChange}
                  placeholder="Duration"
                  min={1}
                  className="!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200"
                />

                <div title="Completion not editable in simulation mode">
                  <NumberInput
                    id="completion"
                    name="completion"
                    label="Completion Percentage"
                    value={completion}
                    onChange={() => {}} // Disabled - no onChange
                    placeholder="Completion Percentage"
                    min={0}
                    max={100}
                    disabled={true}
                    className="!bg-light-surfaceSecondary dark:!bg-slate-800 cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="w-full">
                <Label className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white block">
                  Assignee
                </Label>
                <div title="Assignee not editable in simulation mode">
                  <FormInput
                    id="assignee"
                    name="assignee"
                    type="text"
                    value={selectedAssigneeLabel}
                    onChange={() => {}} // Disabled - no onChange
                    placeholder="No assignee"
                    disabled={true}
                    className="!bg-light-surfaceSecondary dark:!bg-slate-800 cursor-not-allowed opacity-60"
                  />
                </div>
              </div>

              <div className="w-full">
                <Label className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white block">
                  Additional Assignees
                </Label>
                <div title="Additional assignees not editable in simulation mode">
                  <FormInput
                    id="additional_assignees"
                    name="additional_assignees"
                    type="text"
                    value={
                      additional_resources.length > 0 
                        ? sortedAdditionalResourceArr
                            .filter(res => additional_resources.includes(res.value))
                            .map(res => res.label)
                            .join(', ')
                        : 'No additional assignees'
                    }
                    onChange={() => {}} // Disabled - no onChange
                    placeholder="No additional assignees"
                    disabled={true}
                    className="!bg-light-surfaceSecondary dark:!bg-slate-800 cursor-not-allowed opacity-60"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div title="Contract Bid Price not editable in simulation mode">
                  <NumberInput
                    id="bid_price"
                    name="bid_price"
                    label="Contract Bid Price"
                    value={bid_price}
                    onChange={() => {}} // Disabled - no onChange
                    placeholder="Bid price"
                    min={0}
                    max={10000}
                    disabled={true}
                    className="!bg-light-surfaceSecondary dark:!bg-slate-800 cursor-not-allowed opacity-60"
                  />
                </div>

                <div title="Customer Billing Price not editable in simulation mode">
                  <NumberInput
                    id="billing_price"
                    name="billing_price"
                    label="Customer Billing Price"
                    value={billing_price}
                    onChange={() => {}} // Disabled - no onChange
                    placeholder="Billing price"
                    min={0}
                    max={10000}
                    disabled={true}
                    className="!bg-light-surfaceSecondary dark:!bg-slate-800 cursor-not-allowed opacity-60"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 items-center flex gap-4 mt-auto border-t rounded-t-md border-gray-500 dark:border-gray-700 fixed bottom-0 right-0 left-0 bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg">
            <CustomButton
              variant="cancel"
              onClick={handleCloseModal}
              className="hover:bg-light-interactive-hover dark:hover:bg-hover-dark transition-all duration-200"
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={onSubmit}
              className="ml-auto bg-gradient-to-r from-primary-light to-primary-dark hover:from-primary-hover hover:to-primary-dark text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            >
              Update 
            </CustomButton>
          </div>
        </div>
      </Drawer>
    </Portal>
  );
};

export default SimulationTaskModal;
